import { css } from '@emotion/react'
import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { inject, observer } from 'mobx-react'
import { Form, useField, useFormState } from 'react-final-form'
import createDecorator from 'final-form-calculate'
import * as yup from 'yup'

import { API, API_URL } from '../../../api'
import {
  Checkbox,
  Button,
  MultiButtonInput,
  TooltipText,
  PercentInput,
  CurrencyInput,
  SharedModal,
  HelpIcon,
} from '../../../components'
import { numberToDollars, reduceValidationError, withRouterProps } from '../../../utils'
import { Page } from './elements'
import CatchupContributions from './CatchupContributions'
import dayjs from 'dayjs'
import { ButtonsBox } from '../../../components/styled'
import { ModalActions, ModalConfirmButton } from '../../../components/ModalActions'

const ContribLimitText = styled.p`
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  font-weight: 400;
  margin: 25px 0px;
`

const ContribLimitError = styled.p`
  color: red;
  font-size: 14px;
  font-weight: 400;
  margin: 0px 0px 25px 0px;
`

const schema = (enableCatchupContributions, contribMethod, limits, annualSalary) =>
  yup.object({
    rothRate: yup
      .number()
      .nullable()
      .test('percent-sum-limit-rothRate', function (value) {
        const percentLimit = Number(limits?.effectiveRothPreTaxLimitPercent)
        const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
        if (contribMethod !== 'P' || !hasLimit) return true
        const { preTaxRate, afterTaxRate } = this.parent || {}
        const rothRateEntered = Number(value) || 0
        const preTaxRateEntered = Number(preTaxRate) || 0
        const afterTaxRateEntered = Number(afterTaxRate) || 0
        const withinPercentLimit =
          rothRateEntered + preTaxRateEntered + afterTaxRateEntered <= percentLimit
        return (
          withinPercentLimit ||
          this.createError({
            message: '\u200B',
          })
        )
      }),
    rothAmount: yup
      .number()
      .nullable()
      .test('dollar-sum-limit-rothAmount', function (value) {
        const percentLimit = Number(limits?.effectiveRothPreTaxLimitPercent)
        const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
        if (contribMethod !== 'D' || !hasLimit) return true
        const dollarLimit = (percentLimit / 100) * annualSalary
        const { preTaxAmount, afterTaxAmount } = this.parent || {}
        const rothAmountEntered = Number(value) || 0
        const preTaxAmountEntered = Number(preTaxAmount) || 0
        const afterTaxAmountEntered = Number(afterTaxAmount) || 0
        const withinDollarLimit =
          rothAmountEntered + preTaxAmountEntered + afterTaxAmountEntered <= dollarLimit
        return (
          withinDollarLimit ||
          this.createError({
            message: '\u200B',
          })
        )
      }),
    preTaxRate: yup
      .number()
      .nullable()
      .test('percent-sum-limit-preTaxRate', function (value) {
        const percentLimit = Number(limits?.effectiveRothPreTaxLimitPercent)
        const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
        if (contribMethod !== 'P' || !hasLimit) return true
        const { rothRate, afterTaxRate } = this.parent || {}
        const preTaxRateEntered = Number(value) || 0
        const rothRateEntered = Number(rothRate) || 0
        const afterTaxRateEntered = Number(afterTaxRate) || 0
        const withinPercentLimit =
          preTaxRateEntered + rothRateEntered + afterTaxRateEntered <= percentLimit
        return (
          withinPercentLimit ||
          this.createError({
            message: '\u200B',
          })
        )
      }),
    preTaxAmount: yup
      .number()
      .nullable()
      .test('dollar-sum-limit-preTaxAmount', function (value) {
        const percentLimit = Number(limits?.effectiveRothPreTaxLimitPercent)
        const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
        if (contribMethod !== 'D' || !hasLimit) return true
        const dollarLimit = (percentLimit / 100) * annualSalary
        const { rothAmount, afterTaxAmount } = this.parent || {}
        const preTaxAmountEntered = Number(value) || 0
        const rothAmountEntered = Number(rothAmount) || 0
        const afterTaxAmountEntered = Number(afterTaxAmount) || 0
        const withinDollarLimit =
          preTaxAmountEntered + rothAmountEntered + afterTaxAmountEntered <= dollarLimit
        return (
          withinDollarLimit ||
          this.createError({
            message: '\u200B',
          })
        )
      }),
    afterTaxRate: yup
      .number()
      .nullable()
      .test('percent-sum-limit-afterTaxRate', function (value) {
        const percentLimit = Number(limits?.effectiveRothPreTaxLimitPercent)
        const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
        if (contribMethod !== 'P' || !hasLimit) return true
        const { rothRate, preTaxRate } = this.parent || {}
        const afterTaxRateEntered = Number(value) || 0
        const rothRateEntered = Number(rothRate) || 0
        const preTaxRateEntered = Number(preTaxRate) || 0
        const withinPercentLimit =
          afterTaxRateEntered + rothRateEntered + preTaxRateEntered <= percentLimit
        return (
          withinPercentLimit ||
          this.createError({
            message: '\u200B',
          })
        )
      }),
    afterTaxAmount: yup
      .number()
      .nullable()
      .test('dollar-sum-limit-afterTaxAmount', function (value) {
        const percentLimit = Number(limits?.effectiveRothPreTaxLimitPercent)
        const hasLimit = Number.isFinite(percentLimit) && percentLimit > 0
        if (contribMethod !== 'D' || !hasLimit) return true
        const dollarLimit = (percentLimit / 100) * annualSalary
        const { rothAmount, preTaxAmount } = this.parent || {}
        const afterTaxAmountEntered = Number(value) || 0
        const rothAmountEntered = Number(rothAmount) || 0
        const preTaxAmountEntered = Number(preTaxAmount) || 0
        const withinDollarLimit =
          afterTaxAmountEntered + rothAmountEntered + preTaxAmountEntered <= dollarLimit
        return (
          withinDollarLimit ||
          this.createError({
            message: '\u200B',
          })
        )
      }),
    catchupContribStatus: yup
      .string()
      .nullable()
      .test('catchup-required', 'Please select an option', function (value) {
        if (!enableCatchupContributions) return true
        return value === 'Y' || value === 'N'
      }),
    terms: yup
      .boolean()
      .test('terms-required', 'Please check the box above to confirm', function (value) {
        return value === true
      }),
  })

const calculator = createDecorator(
  {
    field: 'contribMethod',
    updates: {
      preTaxRate: () => 0,
      preTaxAmount: () => 0,
      rothRate: () => 0,
      rothAmount: () => 0,
      afterTaxRate: () => 0,
      afterTaxAmount: () => 0,
      catchupContribStatus: () => null,
      catchupPreTaxRate: () => 0,
      catchupPreTaxAmount: () => 0,
      catchupRothRate: () => 0,
      catchupRothAmount: () => 0,
      catchupAfterTaxRate: () => 0,
      catchupAfterTaxAmount: () => 0,
    },
  },
  {
    field: 'preTaxRate',
    updates: {
      preTaxAmount: () => 0,
      catchupContribStatus: () => null,
      catchupPreTaxRate: () => 0,
      catchupPreTaxAmount: () => 0,
      catchupRothRate: () => 0,
      catchupRothAmount: () => 0,
      catchupAfterTaxRate: () => 0,
      catchupAfterTaxAmount: () => 0,
    },
  },
  {
    field: 'preTaxAmount',
    updates: {
      preTaxRate: () => 0,
      catchupContribStatus: () => null,
      catchupPreTaxRate: () => 0,
      catchupPreTaxAmount: () => 0,
      catchupRothRate: () => 0,
      catchupRothAmount: () => 0,
      catchupAfterTaxRate: () => 0,
      catchupAfterTaxAmount: () => 0,
    },
  },
  {
    field: 'rothRate',
    updates: {
      rothAmount: () => 0,
      catchupContribStatus: () => null,
      catchupPreTaxRate: () => 0,
      catchupPreTaxAmount: () => 0,
      catchupRothRate: () => 0,
      catchupRothAmount: () => 0,
      catchupAfterTaxRate: () => 0,
      catchupAfterTaxAmount: () => 0,
    },
  },
  {
    field: 'rothAmount',
    updates: {
      rothRate: () => 0,
      catchupContribStatus: () => null,
      catchupPreTaxRate: () => 0,
      catchupPreTaxAmount: () => 0,
      catchupRothRate: () => 0,
      catchupRothAmount: () => 0,
      catchupAfterTaxRate: () => 0,
      catchupAfterTaxAmount: () => 0,
    },
  },
  {
    field: 'afterTaxRate',
    updates: {
      afterTaxAmount: () => 0,
      catchupContribStatus: () => null,
      catchupPreTaxRate: () => 0,
      catchupPreTaxAmount: () => 0,
      catchupRothRate: () => 0,
      catchupRothAmount: () => 0,
      catchupAfterTaxRate: () => 0,
      catchupAfterTaxAmount: () => 0,
    },
  },
  {
    field: 'afterTaxAmount',
    updates: {
      afterTaxRate: () => 0,
      catchupContribStatus: () => null,
      catchupPreTaxRate: () => 0,
      catchupPreTaxAmount: () => 0,
      catchupRothRate: () => 0,
      catchupRothAmount: () => 0,
      catchupAfterTaxRate: () => 0,
      catchupAfterTaxAmount: () => 0,
    },
  },
  {
    field: 'catchupContribStatus',
    updates: {
      catchupPreTaxRate: (catchupContribStatus, allValues) =>
        catchupContribStatus === 'N' ? 0 : allValues.catchupPreTaxRate,
      catchupPreTaxAmount: (catchupContribStatus, allValues) =>
        catchupContribStatus === 'N' ? 0 : allValues.catchupPreTaxAmount,
      catchupRothRate: (catchupContribStatus, allValues) =>
        catchupContribStatus === 'N' ? 0 : allValues.catchupRothRate,
      catchupRothAmount: (catchupContribStatus, allValues) =>
        catchupContribStatus === 'N' ? 0 : allValues.catchupRothAmount,
      catchupAfterTaxRate: (catchupContribStatus, allValues) =>
        catchupContribStatus === 'N' ? 0 : allValues.catchupAfterTaxRate,
      catchupAfterTaxAmount: (catchupContribStatus, allValues) =>
        catchupContribStatus === 'N' ? 0 : allValues.catchupAfterTaxAmount,
    },
  }
)

const ContribMethodField = () => {
  const { input, meta } = useField('contribMethod', {
    subscription: { value: true, error: true, touched: true },
  })

  return (
    <MultiButtonInput
      name={input.name}
      value={input.value}
      label='Contribution method'
      buttons={[
        { text: '% of Pay', value: 'P' },
        { text: 'Fixed $ Amount', value: 'D' },
      ]}
      tooltip={TooltipText.contributionType()}
      onChange={(name, value) => input.onChange(value)}
      onBlur={input.onBlur}
      error={meta.error}
      showError={meta.touched}
    />
  )
}

const PreTaxSavingsField = () => {
  const { values } = useFormState({ subscription: { values: true } })
  const { input: percentInput, meta: percentMeta } = useField('preTaxRate', {
    parse: v => v,
    format: v => v,
  })
  const { input: amountInput, meta: amountMeta } = useField('preTaxAmount', {
    parse: v => v,
    format: value => (value === null ? undefined : value),
  })

  if (values.contribMethod === 'P') {
    return (
      <div
        css={css`
          display: flex;
          align-items: baseline;
        `}>
        <PercentInput
          name={percentInput.name}
          value={percentInput.value === '' ? null : percentInput.value}
          onChange={(name, value) => percentInput.onChange(value)}
          onBlur={percentInput.onBlur}
          error={percentMeta.error}
          showError={percentMeta.touched}
          width='100px'
        />
        &nbsp;&nbsp;|&nbsp;&nbsp;$
        {numberToDollars((values.annualSalary * percentInput.value) / 100, true)}/year
      </div>
    )
  }

  if (values.contribMethod === 'D') {
    return (
      <div
        css={css`
          display: flex;
          align-items: baseline;
        `}>
        {numberToDollars((amountInput.value / values.annualSalary) * 100, true)}
        %&nbsp;&nbsp;|&nbsp;&nbsp;
        <CurrencyInput
          name={amountInput.name}
          value={amountInput.value}
          placeholder='0'
          onChange={(name, value) => amountInput.onChange(value)}
          onBlur={amountInput.onBlur}
          error={amountMeta.error}
          showError={amountMeta.touched}
          decimalScale={0}
          width='125px'
        />
      </div>
    )
  }

  return (
    <div>
      {numberToDollars(percentInput.value, true)}%&nbsp;&nbsp;|&nbsp;&nbsp;$
      {numberToDollars(amountInput.value, true)}/year
    </div>
  )
}

const RothSavingsField = () => {
  const { values } = useFormState({ subscription: { values: true } })
  const { input: percentInput, meta: percentMeta } = useField('rothRate', {
    parse: v => v,
    format: v => v,
  })
  const { input: amountInput, meta: amountMeta } = useField('rothAmount', {
    parse: v => v,
    format: value => (value === null ? undefined : value),
  })

  if (values.contribMethod === 'P') {
    return (
      <div
        css={css`
          display: flex;
          align-items: baseline;
        `}>
        <PercentInput
          name={percentInput.name}
          value={percentInput.value === '' ? null : percentInput.value}
          onChange={(name, value) => percentInput.onChange(value)}
          onBlur={percentInput.onBlur}
          error={percentMeta.error}
          showError={percentMeta.touched}
          width='100px'
        />
        &nbsp;&nbsp;|&nbsp;&nbsp;$
        {numberToDollars((values.annualSalary * percentInput.value) / 100, true)}/year
      </div>
    )
  }

  if (values.contribMethod === 'D') {
    return (
      <div
        css={css`
          display: flex;
          align-items: baseline;
        `}>
        {numberToDollars((amountInput.value / values.annualSalary) * 100, true)}
        %&nbsp;&nbsp;|&nbsp;&nbsp;
        <CurrencyInput
          name={amountInput.name}
          value={amountInput.value}
          placeholder='0'
          onChange={(name, value) => amountInput.onChange(value)}
          onBlur={amountInput.onBlur}
          error={amountMeta.error}
          showError={amountMeta.touched}
          decimalScale={0}
          width='125px'
        />
      </div>
    )
  }

  return (
    <div>
      {numberToDollars(percentInput.value, true)}%&nbsp;&nbsp;|&nbsp;&nbsp;$
      {numberToDollars(amountInput.value, true)}/year
    </div>
  )
}

const AfterTaxSavingsField = () => {
  const { values } = useFormState({ subscription: { values: true } })
  const { input: percentInput, meta: percentMeta } = useField('afterTaxRate', {
    parse: v => v,
    format: v => v,
  })
  const { input: amountInput, meta: amountMeta } = useField('afterTaxAmount', {
    parse: v => v,
    format: value => (value === null ? undefined : value),
  })

  if (values.contribMethod === 'P') {
    return (
      <div
        css={css`
          display: flex;
          align-items: baseline;
        `}>
        <PercentInput
          name={percentInput.name}
          value={percentInput.value === '' ? null : percentInput.value}
          onChange={(name, value) => percentInput.onChange(value)}
          onBlur={percentInput.onBlur}
          error={percentMeta.error}
          showError={percentMeta.touched}
          width='100px'
        />
        &nbsp;&nbsp;|&nbsp;&nbsp;$
        {numberToDollars((values.annualSalary * percentInput.value) / 100, true)}/year
      </div>
    )
  }

  if (values.contribMethod === 'D') {
    return (
      <div
        css={css`
          display: flex;
          align-items: baseline;
        `}>
        {numberToDollars((amountInput.value / values.annualSalary) * 100, true)}
        %&nbsp;&nbsp;|&nbsp;&nbsp;
        <CurrencyInput
          name={amountInput.name}
          value={amountInput.value}
          placeholder='0'
          onChange={(name, value) => amountInput.onChange(value)}
          onBlur={amountInput.onBlur}
          error={amountMeta.error}
          showError={amountMeta.touched}
          decimalScale={0}
          width='125px'
        />
      </div>
    )
  }

  return (
    <div>
      {numberToDollars(percentInput.value, true)}%&nbsp;&nbsp;|&nbsp;&nbsp;$
      {numberToDollars(amountInput.value, true)}/year
    </div>
  )
}

const TermsField = () => {
  const { submitting } = useFormState({ subscription: { submitting: true } })
  const { input, meta } = useField('terms', {
    subscription: { value: true, error: true, touched: true },
  })

  function handleOnClick() {
    if (submitting) {
      //
    } else {
      input.onFocus()
      input.onChange(!input.value)
      input.onBlur()
    }
  }

  return (
    <div
      css={css`
        border: 6px solid #c4d7e0;
        border-radius: 8px;
        padding: 20px;
        margin-top: 40px;
      `}>
      <div
        css={css`
          color: #022a3a;
          font-size: 24px;
          line-height: 1.35;
        `}>
        Confirmation
      </div>
      <div
        css={css`
          display: flex;
          margin: auto;
          margin: 16px 0;
        `}>
        <div
          css={css`
            margin-top: 8px;
            min-width: 42px;
          `}>
          <Checkbox
            id='checkbox'
            checked={input.value}
            onClick={handleOnClick}
            css={css`
              margin: auto;
            `}
          />
        </div>

        <div
          onClick={handleOnClick}
          css={css`
            cursor: pointer;
            color: #4a606a;
            font-size: 1.125rem;
            font-weight: 300;
            line-height: 1.5;
            padding-left: 12px;
          `}>
          I want to change my contribution rate only at this time. I do not wish to review any other
          information, including the impact of this change on my projected retirement income.
        </div>
      </div>
      <div
        css={css`
          color: #e31e27;
          font-size: 14px;
        `}>
        {meta.touched && meta.error}
      </div>
    </div>
  )
}

const UpdateContributionsOnly = props => {
  const [state, setState] = useState('loading')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [displayContribLimitExceeded, setDisplayContribLimitExceeded] = useState(false)

  async function fetchInitialState() {
    try {
      await props.store.getInstitutionalAccount()
      await props.store.getLimits()
      setState('done')
    } catch (err) {
      console.error(err)
      setState('error')
    }
  }

  useEffect(() => {
    fetchInitialState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (state === 'loading') {
    return <Page.Loading />
  }

  if (state === 'error') {
    return <Page.ErrorMessage>Oops! Something went wrong, please try again later</Page.ErrorMessage>
  }

  if (state === 'submitted') {
    return (
      <Page>
        <Page.Header />
        <Page.Title>You're all set!</Page.Title>
        <div
          css={css`
            color: #4a606a;
            font-size: 1.125rem;
            font-weight: 300;
            line-height: 1.35;
            margin: 48px 0 24px;
          `}>
          Your transaction has been submitted. You should receive a confirmation email shortly.
        </div>
        <div
          css={css`
            color: #4a606a;
            font-size: 0.875rem;
            font-weight: 300;
            line-height: 1.45;
            margin: 24px 0 40px;
          `}>
          Be aware: Depending upon the arrangement between GuidedChoice and your plan, your
          transaction could take 24-48 hours to process, or as many as 10 business days.
        </div>
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            padding: 12px 40px;
            @media (max-width: 768px) {
              flex-wrap: wrap;
              justify-content: center;
            }
          `}>
          <Button
            type='button'
            variant='primary'
            onClick={() => window.open(`${API_URL.slice(0, -4)}logout`, '_self')}>
            Log off
          </Button>
        </div>
      </Page>
    )
  }

  const {
    config: { enableCatchupContributions },
    institutional: {
      name,
      contribMethod,
      pretaxSavingsRate,
      preTaxSavingsAmt,
      rothContribAllowed,
      rothSavingsRate,
      rothSavingsAmt,
      posttaxContribAllowed,
      posttaxSavingsRate,
      postTaxSavingsAmt,
    },
    account: { annualSalary },
    plan: { contribMethodChangeAllowed, limits },
    standardLimit,
    standardCatchup,
    additionalCatchup,
  } = props.store

  const deriveAnnualAmountFromRate = rate => {
    const salary = Number(annualSalary)
    const rateNumber = Number(rate)

    if (!Number.isFinite(salary) || !Number.isFinite(rateNumber)) {
      return null
    }

    return (salary * rateNumber) / 100
  }

  const currentPreTaxAnnualAmount =
    contribMethod === 'P'
      ? (deriveAnnualAmountFromRate(pretaxSavingsRate) ?? preTaxSavingsAmt)
      : preTaxSavingsAmt
  const currentRothAnnualAmount =
    contribMethod === 'P'
      ? (deriveAnnualAmountFromRate(rothSavingsRate) ?? rothSavingsAmt)
      : rothSavingsAmt
  const currentAfterTaxAnnualAmount =
    contribMethod === 'P'
      ? (deriveAnnualAmountFromRate(posttaxSavingsRate) ?? postTaxSavingsAmt)
      : postTaxSavingsAmt

  const annualCompLimitApplied = annualSalary > (limits?.annualContributionLimit ?? 0)
  const maximumAmount = additionalCatchup.maximumAmount ?? 0

  const birthYear = dayjs(props.store.person.birthDate).year()
  const currentYear = dayjs().year()
  const ageThisYear = currentYear - birthYear

  const initialValues = () => {
    return {
      contribMethod,
      annualSalary,
      preTaxRate: 0,
      preTaxAmount: 0,
      rothRate: 0,
      rothAmount: 0,
      afterTaxRate: 0,
      afterTaxAmount: 0,
      catchupContribStatus: null,
      catchupPreTaxRate: 0,
      catchupPreTaxAmount: 0,
      catchupRothRate: 0,
      catchupRothAmount: 0,
      catchupAfterTaxRate: 0,
      catchupAfterTaxAmount: 0,
      terms: false,
    }
  }

  function validate(values) {
    return schema(enableCatchupContributions, values.contribMethod, limits, annualSalary)
      .validate(values, { abortEarly: false })
      .then(() => {
        setDisplayContribLimitExceeded(false)
        return {}
      })
      .catch(err => {
        const errors = reduceValidationError(err)
        // Check if any of the errors are the contribution limit errors (message === '\u200B')
        const hasLimitError = Object.values(errors).some(error => error === '\u200B')
        setDisplayContribLimitExceeded(hasLimitError)
        return errors
      })
  }

  function onSubmit(values) {
    if (values.catchupPreTaxRate > values.preTaxRate) {
      setIsModalOpen(true)
    } else {
      return changeSavingsRate(values)
    }
  }

  function buildSavingsRate(values) {
    const mapTaxType = { preTax: 1, afterTax: 2, roth: 5 }
    const preTaxValue = { P: values.preTaxRate, D: values.preTaxAmount }
    const rothValue = { P: values.rothRate, D: values.rothAmount }
    const afterTaxValue = { P: values.afterTaxRate, D: values.afterTaxAmount }
    const catchupPreTaxValue = { P: values.catchupPreTaxRate, D: values.catchupPreTaxAmount }
    const catchupRothValue = { P: values.catchupRothRate, D: values.catchupRothAmount }
    const catchupAfterTaxValue = { P: values.catchupAfterTaxRate, D: values.catchupAfterTaxAmount }

    return {
      [mapTaxType.preTax]: {
        taxType: mapTaxType.preTax,
        deferralType: values.contribMethod,
        value: preTaxValue[values.contribMethod] - catchupPreTaxValue[values.contribMethod],
      },

      [mapTaxType.roth]: rothContribAllowed
        ? {
            taxType: mapTaxType.roth,
            deferralType: values.contribMethod,
            value: rothValue[values.contribMethod] - catchupRothValue[values.contribMethod],
          }
        : undefined,

      [mapTaxType.afterTax]: posttaxContribAllowed
        ? {
            taxType: mapTaxType.afterTax,
            deferralType: values.contribMethod,
            value: afterTaxValue[values.contribMethod] - catchupAfterTaxValue[values.contribMethod],
          }
        : undefined,
    }
  }

  function buildCatchUp(values) {
    const mapTaxType = { preTax: 1, afterTax: 2, roth: 5 }
    const preTaxValue = { P: values.catchupPreTaxRate, D: values.catchupPreTaxAmount }
    const rothValue = { P: values.catchupRothRate, D: values.catchupRothAmount }
    const afterTaxValue = { P: values.catchupAfterTaxRate, D: values.catchupAfterTaxAmount }

    return {
      [mapTaxType.preTax]: {
        taxType: mapTaxType.preTax,
        deferralType: values.contribMethod,
        value: preTaxValue[values.contribMethod],
      },

      [mapTaxType.roth]: rothContribAllowed
        ? {
            taxType: mapTaxType.roth,
            deferralType: values.contribMethod,
            value: rothValue[values.contribMethod],
          }
        : undefined,

      [mapTaxType.afterTax]: posttaxContribAllowed
        ? {
            taxType: mapTaxType.afterTax,
            deferralType: values.contribMethod,
            value: afterTaxValue[values.contribMethod],
          }
        : undefined,
    }
  }

  async function changeSavingsRate(values) {
    try {
      await API.post('transactions/change-savings-rate', {
        savingsRate: buildSavingsRate(values),
        catchUp: buildCatchUp(values),
      })

      setState('submitted')
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <Page>
      <Page.Header />

      <Page.Title
        css={css`
          margin: 40px 0 50px;
        `}>
        Change how much I'm saving only
      </Page.Title>

      <div
        css={css`
          color: #4a606a;
          font-size: 24px;
          line-height: 1.25;
        `}>
        {name}
      </div>

      <ContribLimitText>
        Your plan does not allow you to contribute more than{' '}
        {limits?.effectiveRothPreTaxLimitPercent}% of your paycheck.
      </ContribLimitText>
      {displayContribLimitExceeded && (
        <ContribLimitError>
          Total contributions cannot exceed {limits?.effectiveRothPreTaxLimitPercent}%.
        </ContribLimitError>
      )}

      <Form
        initialValues={initialValues()}
        decorators={[calculator]}
        validate={validate}
        onSubmit={onSubmit}
        subscription={{ submitting: true, submitError: true }}
        render={({ handleSubmit, submitting }) => (
          <div>
            {contribMethodChangeAllowed && (
              <div
                css={css`
                  margin: 1.5rem 0 0;
                `}>
                <ContribMethodField />
              </div>
            )}

            <div
              css={css`
                display: flex;
                color: #4a606a;
                font-weight: 300;
                font-size: 1.125rem;
                line-height: 1.25;
                margin: 1rem 0;
              `}>
              <div
                css={css`
                  width: 300px;
                `}
              />
              <div
                css={css`
                  width: 300px;
                `}>
                Contributing now
              </div>
              <div
                css={css`
                  width: 350px;
                `}>
                Change to
              </div>
            </div>

            <div
              css={css`
                display: flex;
                align-items: baseline;
                color: #4a606a;
                font-weight: 300;
                font-size: 1.125rem;
                line-height: 1.25;
                margin: 1rem 0;
              `}>
              <div
                css={css`
                  width: 300px;
                `}>
                Pre-tax rate
              </div>
              <div
                css={css`
                  width: 300px;
                `}>
                {numberToDollars(pretaxSavingsRate, true)}% | $
                {numberToDollars(currentPreTaxAnnualAmount, true)}/year
              </div>
              <div
                css={css`
                  width: 350px;
                `}>
                <PreTaxSavingsField />
              </div>
            </div>

            {rothContribAllowed && (
              <div
                css={css`
                  display: flex;
                  align-items: baseline;
                  color: #4a606a;
                  font-weight: 300;
                  font-size: 1.125rem;
                  line-height: 1.25;
                  margin: 1rem 0;
                `}>
                <div
                  css={css`
                    width: 300px;
                  `}>
                  Roth rate
                </div>
                <div
                  css={css`
                    width: 300px;
                  `}>
                  {numberToDollars(rothSavingsRate, true)}% | $
                  {numberToDollars(currentRothAnnualAmount, true)}/year
                </div>
                <div
                  css={css`
                    width: 350px;
                  `}>
                  <RothSavingsField />
                </div>
              </div>
            )}

            {posttaxContribAllowed && (
              <div
                css={css`
                  color: #4a606a;
                  font-size: 18px;
                  line-height: 1.35;
                  margin: 2.5rem 0;
                `}>
                <span
                  css={css`
                    font-weight: 600;
                  `}>
                  IMPORTANT:
                </span>{' '}
                Before entering an after-tax contribution rate, be aware that in nearly all cases,
                it will be to your tax advantage to save up to the maximum allowed using pre-tax
                {rothContribAllowed ? ' and/or Roth' : ''} contributions before saving more with
                after-tax contributions.
              </div>
            )}

            {posttaxContribAllowed && (
              <div
                css={css`
                  display: flex;
                  align-items: baseline;
                  color: #4a606a;
                  font-weight: 300;
                  font-size: 1.125rem;
                  line-height: 1.25;
                  margin: 1rem 0;
                `}>
                <div
                  css={css`
                    width: 300px;
                  `}>
                  After-tax rate
                </div>
                <div
                  css={css`
                    width: 300px;
                  `}>
                  {numberToDollars(posttaxSavingsRate, true)}% | $
                  {numberToDollars(currentAfterTaxAnnualAmount, true)}/year
                </div>
                <div
                  css={css`
                    width: 350px;
                  `}>
                  <AfterTaxSavingsField />
                </div>
              </div>
            )}

            <div
              css={css`
                color: #7a8e96;
                font-size: 12px;
                line-height: 1.5;
              `}>
              *Contribution amounts shown are annualized and rounded up. The actual contribution
              total will be limited to IRS and plan rule maximums. In {limits.year}, the limit is $
              {numberToDollars(limits.dlr401k, true)}; if age {limits.catchupAge}+ in the calendar
              year, you may add up to an additional $
              {numberToDollars(limits.catchupLimitAmount, true)} in catch-up contributions.
            </div>
            {ageThisYear >= 60 && ageThisYear < 64 && (
              <div
                css={css`
                  color: #7a8e96;
                  font-size: 12px;
                  line-height: 1.5;
                  margin-top: 10px;
                `}>
                Be aware: As you are age 60, 61, 62, or 63 this calendar year, the IRS allows you to
                make an additional catch-up contribution to your company’s retirement plan of up to{' '}
                $
                {(
                  additionalCatchup?.maximumAmount - standardCatchup?.maximumAmount
                ).toLocaleString()}{' '}
                for a total catch-up contribution of up to $
                {additionalCatchup?.maximumAmount?.toLocaleString()}, making the retirement plan
                maximum contribution $
                {(standardLimit + additionalCatchup?.maximumAmount).toLocaleString()}.
              </div>
            )}
            {ageThisYear >= 64 && (
              <div
                css={css`
                  color: #7a8e96;
                  font-size: 12px;
                  line-height: 1.5;
                  margin-top: 10px;
                `}>
                Be aware: You may need to reduce your retirement plan contributions. The IRS permits
                those aged 64+ in the current calendar year to make up to the regular catch-up
                contribution limit only. With the standard contribution limit of $
                {standardLimit?.toLocaleString()}, this means the maximum contribution you can make
                to your company’s retirement plan is $
                {(standardLimit + standardCatchup?.maximumAmount)?.toLocaleString()}.
              </div>
            )}

            {annualCompLimitApplied && (
              <div style={{ marginTop: 20 }}>
                <HelpIcon
                  helpLabel={'Annual compensation limit applied'}
                  tooltip={TooltipText.annualCompensationLimitApplied({
                    compensationLimit: limits?.annualContributionLimit ?? 0,
                    currentYear: limits.year,
                    electiveDeferrals: limits.dlr401k,
                    catchUp50Up: limits.catchupLimitAmount,
                    catchUp60To63: maximumAmount,
                  })}
                />
              </div>
            )}

            {enableCatchupContributions && <CatchupContributions />}

            <TermsField />

            <SharedModal
              isModalOpen={isModalOpen}
              title={`Uh Oh!`}
              toggleModal={() => setIsModalOpen(modal => !modal)}>
              <Page.WarningText>
                It looks like the value you entered for catch-up contributions exceeds the value
                you're deducting from your paycheck. Please check the values you entered and try
                again.
              </Page.WarningText>
              <ModalActions>
                <ModalConfirmButton type='button' onClick={() => setIsModalOpen(false)}>
                  Got it!
                </ModalConfirmButton>
              </ModalActions>
            </SharedModal>

            <ButtonsBox>
              <Button
                variant='secondary'
                type='button'
                onClick={() => props.navigate(-1)}
                disabled={submitting}>
                Cancel
              </Button>
              <Button variant='primary' type='button' onClick={handleSubmit} disabled={submitting}>
                Next
              </Button>
            </ButtonsBox>
          </div>
        )}
      />
    </Page>
  )
}

export default withRouterProps(inject('store')(observer(UpdateContributionsOnly)))
